import { Input } from "@ondit/ui";
import { type ClipboardEvent, type KeyboardEvent, useRef, useState } from "react";
import { useT } from "~/core/i18n";

/**
 * Six boxes for a six-digit code.
 *
 * One input per digit rather than one field with a pattern, because the mail
 * shows the code as six spaced digits and the boxes let the eye match them one
 * at a time. Paste is handled so a copied "042199" lands in all six; typing
 * advances, backspace retreats, and the last digit submits without a button —
 * the person has already told us everything.
 */
export function CodeEntry({ disabled, onComplete }: { disabled?: boolean; onComplete: (code: string) => void }) {
	const t = useT();
	const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
	const boxes = useRef<(HTMLInputElement | null)[]>([]);

	function commit(next: string[]) {
		setDigits(next);
		if (next.every((d) => d !== "")) onComplete(next.join(""));
	}

	function onChange(index: number, value: string) {
		const clean = value.replace(/\D/g, "");
		const next = [...digits];
		if (!clean) {
			next[index] = "";
			setDigits(next);
			return;
		}
		// A phone keyboard can deliver several digits into one box.
		for (let i = 0; i < clean.length && index + i < 6; i++) next[index + i] = clean[i];
		commit(next);
		boxes.current[Math.min(index + clean.length, 5)]?.focus();
	}

	function onKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
		if (event.key === "Backspace" && digits[index] === "" && index > 0) {
			boxes.current[index - 1]?.focus();
		}
		// The boxes sit inside the sign-in form, and Enter in a form field
		// submits the form — which re-registers the address and throws the
		// person out of verification. The last digit is the submit here.
		if (event.key === "Enter") event.preventDefault();
	}

	function onPaste(event: ClipboardEvent<HTMLInputElement>) {
		const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
		if (pasted.length === 0) return;
		event.preventDefault();
		const next: string[] = Array(6).fill("");
		for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
		commit(next);
		boxes.current[Math.min(pasted.length, 5)]?.focus();
	}

	return (
		<div className="field">
			<span className="label">{t("login.code")}</span>
			<div className="code-entry">
				{digits.map((digit, index) => (
					<Input
						className="w-(--control-h) text-center"
						// biome-ignore lint/suspicious/noArrayIndexKey: the boxes are positional by definition
						key={index}
						ref={(el) => {
							boxes.current[index] = el;
						}}
						inputMode="numeric"
						autoComplete={index === 0 ? "one-time-code" : "off"}
						maxLength={6}
						aria-label={t("login.codeDigit", { n: String(index + 1) })}
						value={digit}
						disabled={disabled}
						onChange={(e) => onChange(index, e.target.value)}
						onKeyDown={(e) => onKeyDown(index, e)}
						onPaste={onPaste}
						onFocus={(e) => e.target.select()}
					/>
				))}
			</div>
			<span className="hint">{t("login.codeHint")}</span>
		</div>
	);
}
